/**
 * Shared chart series palette — single source of truth for Recharts colors.
 * Design system: Lodge Orange is the only brand accent. Secondary/tertiary
 * series use the onyx neutral ramp (never blue/purple). Green/red are reserved
 * for semantic positive/negative only.
 *
 * Recharts emits SVG presentation attributes where CSS variables don't
 * resolve, so both modes live here as literal hex: CHART_COLORS (light) and
 * CHART_COLORS_DARK, same keys. Pick via useChartTheme()/useIsDarkMode() —
 * never patch dark values inline at the call site.
 *
 * Hex values mirror frontend/src/styles/theme.css.
 */
export const CHART_COLORS = {
  primary: '#FF7A00',   // --lodge-orange-400
  primaryLight: '#FFA34D', // --lodge-orange-300 (gradient stops / fills)
  secondary: '#26262D', // --lodge-onyx-700 (2nd series — strong neutral, contrasts with orange + light benchmarks)
  tertiary: '#72727F',  // --lodge-onyx-400 (3rd series / benchmark — neutral)
  grid: '#E8E8F0',      // --lodge-onyx-100
  axis: '#72727F',      // --lodge-onyx-400
  positive: '#12B76A',  // --color-success
  negative: '#F04438',  // --color-error
};

// Dark counterpart: orange stays orange (it pops on onyx — never whitened),
// neutrals lighten to stay visible, grid drops to the dark border hairline.
export const CHART_COLORS_DARK = {
  primary: '#FF7A00',   // unchanged — fills keep the vivid brand accent
  primaryLight: '#FFA34D',
  secondary: '#B8B8C8', // onyx-700 is invisible on dark surfaces — lightened
  tertiary: '#8888A0',
  grid: '#2A2A33',      // --border-subtle
  axis: '#8888A0',      // 4.4:1 on the dark canvas
  positive: '#12B76A',
  negative: '#F04438',
};

// New (inset) shell: no orange. Series read as ink on the white card —
// primary = onyx-900, then two neutral steps — matching the shell's black
// primary. Same keys as CHART_COLORS; semantic green/red unchanged.
export const CHART_COLORS_INK = {
  primary: '#0F0F11',      // --lodge-onyx-900
  primaryLight: '#52525F', // --lodge-onyx-500 (gradient stops / fills)
  secondary: '#8E8E9C',    // between onyx-400 and onyx-300
  tertiary: '#C8C8D4',     // --lodge-onyx-200 (benchmarks)
  grid: '#E8E8F0',
  axis: '#72727F',
  positive: '#12B76A',
  negative: '#F04438',
};

export const CHART_COLORS_INK_DARK = {
  primary: '#F0F0F5',
  primaryLight: '#A0A0AE',
  secondary: '#7C7C8C',
  tertiary: '#3F3F4A',
  grid: '#2A2A33',
  axis: '#8888A0',
  positive: '#12B76A',
  negative: '#F04438',
};

const NEUTRAL_AXES = { grid: '#E8E8F0', axis: '#72727F', positive: '#12B76A', negative: '#F04438' };
const NEUTRAL_AXES_DARK = { grid: '#2A2A33', axis: '#8888A0', positive: '#12B76A', negative: '#F04438' };

/**
 * Chart palettes — Settings → Appearance → Charts (New layout only; Classic
 * always draws Lodge). `light`/`dark` share CHART_COLORS' keys. The id lands
 * on html[data-chart-palette] (ThemeContext + the pre-paint script) and
 * index.css remaps the bklit --chart-* tokens per id.
 */
// Lodge is what the bare --chart-* tokens draw: Classic, and any page with no
// data-chart-palette attribute. Apricot is the New look's default preference
// (ThemeContext sets the attribute for it; Lodge orange reads too aggressive
// as a default).
export const LODGE_CHART_PALETTE = 'lodge';
export const DEFAULT_CHART_PALETTE = 'apricot';
export const CHART_PALETTES = {
  apricot: {
    name: 'Apricot', description: 'Soft orange lead, light greys',
    light: { primary: '#F2A46B', primaryLight: '#F7C39A', secondary: '#A0A0AE', tertiary: '#D0D0DA', ...NEUTRAL_AXES },
    dark: { primary: '#F5B27E', primaryLight: '#F9CBA8', secondary: '#8888A0', tertiary: '#4A4A58', ...NEUTRAL_AXES_DARK },
  },
  lodge: { name: 'Lodge', description: 'Orange lead, onyx support', light: CHART_COLORS, dark: CHART_COLORS_DARK },
  ink: { name: 'Ink', description: 'Black lead, grey support', light: CHART_COLORS_INK, dark: CHART_COLORS_INK_DARK },
  graphite: {
    name: 'Graphite', description: 'Soft mono: mid-grey lead, light greys',
    light: { primary: '#52525F', primaryLight: '#8A8A98', secondary: '#A0A0AE', tertiary: '#D0D0DA', ...NEUTRAL_AXES },
    dark: { primary: '#C8C8D4', primaryLight: '#E0E0EA', secondary: '#8888A0', tertiary: '#4A4A58', ...NEUTRAL_AXES_DARK },
  },
  mist: {
    name: 'Mist', description: 'Cool steel-grey lead, pale blues',
    light: { primary: '#6B7A90', primaryLight: '#98A5B8', secondary: '#B3BCCA', tertiary: '#D9DEE6', ...NEUTRAL_AXES },
    dark: { primary: '#B7C3D3', primaryLight: '#D2DBE6', secondary: '#7E8A9C', tertiary: '#444C58', ...NEUTRAL_AXES_DARK },
  },
  sage: {
    name: 'Sage', description: 'Green-grey lead, pale greens',
    light: { primary: '#7B9C8A', primaryLight: '#A5BFB1', secondary: '#BFD0C6', tertiary: '#DEE7E1', ...NEUTRAL_AXES },
    dark: { primary: '#A9C7B6', primaryLight: '#C8DDD0', secondary: '#7A9686', tertiary: '#3F4C45', ...NEUTRAL_AXES_DARK },
  },
  linen: {
    name: 'Linen', description: 'Warm sand lead, pale tans',
    light: { primary: '#B0916E', primaryLight: '#CDB79C', secondary: '#D9CBB8', tertiary: '#ECE4D8', ...NEUTRAL_AXES },
    dark: { primary: '#D4BFA0', primaryLight: '#E6D7C2', secondary: '#9C8A70', tertiary: '#4D463C', ...NEUTRAL_AXES_DARK },
  },
};
export const CHART_PALETTE_IDS = Object.keys(CHART_PALETTES);
export const CHART_PALETTE_OPTIONS = CHART_PALETTE_IDS.map((id) => ({ id, ...CHART_PALETTES[id] }));

// 'custom' = the viewer's own lead colour (Settings → Appearance → Data
// colours). ThemeContext derives the series colours (utils/sidebarPalette
// deriveChartPalette) and registers them here so chartColorsFor() and the
// hooks resolve it like a preset; the --chart-* tokens go inline on <html>.
export const CUSTOM_CHART_PALETTE = 'custom';
let customChartPalette = null;
export const registerCustomChartPalette = (palette) => { customChartPalette = palette || null; };
const paletteById = (id) => (id === CUSTOM_CHART_PALETTE ? customChartPalette : CHART_PALETTES[id]);

/** Active palette id from the document (absent attribute = Lodge, incl. Classic). */
export const activeChartPalette = () => {
  if (typeof document === 'undefined') return LODGE_CHART_PALETTE;
  const id = document.documentElement.getAttribute('data-chart-palette');
  return id && paletteById(id) ? id : LODGE_CHART_PALETTE;
};

/** True under the New (inset) shell — html[data-shell='inset'], set pre-paint. */
export const isInkShell = () =>
  typeof document !== 'undefined' && document.documentElement.getAttribute('data-shell') === 'inset';

/** Mode-aware accessor for non-hook call sites, honouring the chosen palette. */
export const chartColorsFor = (dark, paletteId = activeChartPalette()) => {
  const pal = paletteById(paletteId) || CHART_PALETTES[LODGE_CHART_PALETTE];
  return dark ? pal.dark : pal.light;
};

// Ordered palette for multi-series charts (orange first, then neutrals).
export const CHART_SERIES = [
  CHART_COLORS.primary,
  CHART_COLORS.secondary,
  CHART_COLORS.tertiary,
];

export const CHART_SERIES_DARK = [
  CHART_COLORS_DARK.primary,
  CHART_COLORS_DARK.secondary,
  CHART_COLORS_DARK.tertiary,
];

export default CHART_COLORS;
